import { useCallback, useMemo, useState } from 'react';
import {
  FEATURE_SCREEN_LOCK,
  SCREEN_LOCK_COOLDOWN_AFTER_SKIP_MINUTES,
  SCREEN_LOCK_MAX_PER_SESSION_DESKTOP,
  SCREEN_LOCK_MAX_PER_SESSION_MOBILE,
  SCREEN_LOCK_MIN_ITEMS_BEFORE_FIRST,
  SCREEN_LOCK_MIN_MINUTES_BETWEEN_DESKTOP,
  SCREEN_LOCK_MIN_MINUTES_BETWEEN_MOBILE,
  SCREEN_LOCK_RELEVANCE_THRESHOLD,
  SCREEN_LOCK_X_DEFAULT_DESKTOP,
  SCREEN_LOCK_X_DEFAULT_MOBILE,
  SCREEN_LOCK_X_MAX,
  SCREEN_LOCK_X_MIN,
} from '../config/flags';
import type { ScreenLockOutcome, ScreenLockState } from '../types/feedTools';

const STORAGE_VERSION = 'v1';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

interface UseFeedScreenLockParams {
  userId?: string;
  isMobile: boolean;
  enabled?: boolean;
}

interface UseFeedScreenLockResult {
  enabled: boolean;
  shouldShowLock: boolean;
  markShown: () => void;
  registerItemView: (relevanceScore: number) => void;
  registerOutcome: (outcome: ScreenLockOutcome) => void;
}

export function useFeedScreenLock({ userId, isMobile, enabled = true }: UseFeedScreenLockParams): UseFeedScreenLockResult {
  const [shouldShowLock, setShouldShowLock] = useState(false);

  const storageKey = useMemo(() => {
    if (!userId) return null;
    return `screenLockState_${STORAGE_VERSION}_${userId}_${isMobile ? 'mobile' : 'desktop'}`;
  }, [userId, isMobile]);

  const readState = useCallback((): ScreenLockState => {
    const baseX = isMobile ? SCREEN_LOCK_X_DEFAULT_MOBILE : SCREEN_LOCK_X_DEFAULT_DESKTOP;
    const initial: ScreenLockState = {
      xItems: baseX,
      baseXItems: baseX,
      itemsSinceLastLock: 0,
      locksThisSession: 0,
      lastLockAtMs: 0,
      cooldownUntilMs: 0,
      engagementIndex: 0,
      fastSkipStreak: 0,
      device: isMobile ? 'mobile' : 'desktop',
    };
    if (!storageKey) return initial;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return initial;
      const parsed = JSON.parse(raw) as ScreenLockState;
      return {
        ...initial,
        ...parsed,
        device: isMobile ? 'mobile' : 'desktop',
      };
    } catch {
      return initial;
    }
  }, [isMobile, storageKey]);

  const writeState = useCallback((state: ScreenLockState) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore localStorage write failures
    }
  }, [storageKey]);

  const registerItemView = useCallback((relevanceScore: number) => {
    if (!FEATURE_SCREEN_LOCK || !enabled || !userId) return;
    const now = Date.now();
    const state = readState();
    const minGapMs = (isMobile ? SCREEN_LOCK_MIN_MINUTES_BETWEEN_MOBILE : SCREEN_LOCK_MIN_MINUTES_BETWEEN_DESKTOP) * 60_000;
    const maxPerSession = isMobile ? SCREEN_LOCK_MAX_PER_SESSION_MOBILE : SCREEN_LOCK_MAX_PER_SESSION_DESKTOP;

    state.itemsSinceLastLock += 1;

    const canTrigger = state.itemsSinceLastLock >= Math.max(SCREEN_LOCK_MIN_ITEMS_BEFORE_FIRST, state.xItems)
      && now >= state.cooldownUntilMs
      && (state.lastLockAtMs === 0 || now - state.lastLockAtMs >= minGapMs)
      && state.locksThisSession < maxPerSession
      && relevanceScore >= SCREEN_LOCK_RELEVANCE_THRESHOLD;

    if (canTrigger) {
      setShouldShowLock(true);
    }

    writeState(state);
  }, [enabled, isMobile, readState, userId, writeState]);

  const markShown = useCallback(() => {
    if (!FEATURE_SCREEN_LOCK || !enabled || !userId) return;
    const now = Date.now();
    const state = readState();
    state.itemsSinceLastLock = 0;
    state.lastLockAtMs = now;
    state.locksThisSession += 1;
    writeState(state);
    setShouldShowLock(false);
  }, [enabled, readState, userId, writeState]);

  const registerOutcome = useCallback((outcome: ScreenLockOutcome) => {
    if (!FEATURE_SCREEN_LOCK || !enabled || !userId) return;
    const state = readState();
    let responseScore = 0;
    if (outcome === 'continue') {
      responseScore = 2;
      state.fastSkipStreak = 0;
    } else if (outcome === 'scroll') {
      responseScore = -1;
      state.fastSkipStreak += 1;
      state.cooldownUntilMs = Date.now() + SCREEN_LOCK_COOLDOWN_AFTER_SKIP_MINUTES * 60_000;
    } else {
      responseScore = -2;
      state.fastSkipStreak += 1;
      state.cooldownUntilMs = Date.now() + SCREEN_LOCK_COOLDOWN_AFTER_SKIP_MINUTES * 60_000;
    }

    state.engagementIndex = 0.8 * state.engagementIndex + 0.2 * responseScore;
    const targetX = state.baseXItems - 2.5 * state.engagementIndex;
    state.xItems = clamp(Math.round(targetX), SCREEN_LOCK_X_MIN, SCREEN_LOCK_X_MAX);

    if (state.fastSkipStreak >= 2) {
      state.xItems = clamp(Math.round(state.xItems * 1.5), SCREEN_LOCK_X_MIN, SCREEN_LOCK_X_MAX);
      state.fastSkipStreak = 0;
    }

    writeState(state);
  }, [enabled, readState, userId, writeState]);

  return {
    enabled: FEATURE_SCREEN_LOCK && enabled && Boolean(userId),
    shouldShowLock,
    markShown,
    registerItemView,
    registerOutcome,
  };
}
